import { useState, type ReactNode } from "react";
import { Popover, SubmenuTrigger } from "react-aria-components";
import { RxTriangleDown } from "react-icons/rx";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "../core/basic/Button";
import { Menu, MenuButton, MenuItem, Separator } from "../core/basic/Menu";
import { Modal, type Props as ModalProps } from "../core/basic/Modal";
import { popoverStyles } from "../fields/styles/styles";

type BulkSelection<T> = T[] | "all";
export type BulkActionOption<T> = ModalAction<T> | CustomAction<T> | BulkActionDivider;

interface BulkActionGroup<T> {
  id?: string;
  group: string;
  actions: BulkActionOptions<T>[];
  hidden?: boolean;
}

export type BulkActionOptions<T> = BulkActionOption<T> | BulkActionGroup<T>;

type ResolvedOption<T> = {
  option: BulkActionOption<T>;
  resolvedId: string | null;
  resolvedLabel: ReactNode | null;
  index: number;
};

interface ModalAction<T> {
  /** Needed if two options use same label, otherwise uses label as id */
  id?: string;
  label: string | ((selected: T[]) => string);
  action: (selected: BulkSelection<T>) => void | Promise<void>;
  confirmationMessage: ReactNode;
  actionFormFields?: ReactNode;
  disableConfirmButton?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  divider?: never;
}

interface CustomAction<T> {
  /** Needed if two options use same label, otherwise uses label as id */
  id?: string;
  label: ReactNode | ((selected: T[]) => ReactNode);
  action: (selected: BulkSelection<T>) => void | Promise<void>;
  hidden?: boolean;
  disabled?: boolean;

  divider?: never;
  confirmationMessage?: never;
  actionFormFields?: never;
  disableConfirmButton?: never;
}

interface BulkActionDivider {
  id?: never;
  label?: never;
  divider: true;
  hidden?: boolean;
}

interface Props<T> {
  selected?: BulkSelection<T>;
  selectedCount?: number;
  options?: BulkActionOptions<T>[];
  hidden?: boolean;
  isDisabled?: boolean;
}

export const BulkActionButton = <T,>({
  selected = [] as T[],
  selectedCount,
  options = [],
  hidden,
  isDisabled,
}: Props<T>) => {
  const { t } = useTranslation(["common"]);
  const [openOptionId, setOpenOptionId] = useState<string | null>(null);
  if (hidden) {
    return null;
  }

  const collectLeafOptions = (items: BulkActionOptions<T>[]): BulkActionOption<T>[] =>
    items.flatMap((item) => {
      if ("actions" in item) {
        if (item.hidden) {
          return [];
        }
        return collectLeafOptions(item.actions);
      }
      return [item];
    });

  const leafOptions = collectLeafOptions(options);
  const visibleOptions = leafOptions.filter((option) => !option.hidden);
  const selectedArray = selected === "all" ? ([] as T[]) : selected;
  const resolvedOptions: ResolvedOption<T>[] = visibleOptions.map((option, index) => {
    if (option.divider) {
      return { option, resolvedId: null, resolvedLabel: null, index };
    }
    const resolvedLabel = typeof option.label === "function" ? option.label(selectedArray) : option.label;
    const resolvedId = option.id ?? (typeof resolvedLabel === "string" ? resolvedLabel : `option-${index}`);

    return { option, resolvedId, resolvedLabel, index };
  });
  const openOption = resolvedOptions.find(({ option, resolvedId }) => {
    if (option.divider) return false;
    return resolvedId === openOptionId;
  })?.option as ModalAction<T> | undefined;
  const resolvedByOption = new Map<BulkActionOption<T>, ResolvedOption<T>>();
  resolvedOptions.forEach((meta) => resolvedByOption.set(meta.option, meta));

  const menuPopoverClassName = cx(
    popoverStyles,
    css({
      border: "1px solid {colors.border.default}",
      borderRadius: "standard",
      minWidth: "50px",
      paddingY: "xsmall",
      boxShadow: "card",
    }),
  );

  const renderMenuItem = (item: BulkActionOptions<T>): ReactNode => {
    if ("actions" in item) {
      if (item.hidden) {
        return null;
      }
      const children = item.actions.map(renderMenuItem).filter((child): child is ReactNode => child !== null);
      if (children.length === 0) {
        return null;
      }
      return (
        <SubmenuTrigger key={item.id ?? item.group}>
          <MenuItem>{item.group}</MenuItem>
          <Popover placement="left top" className={menuPopoverClassName} shouldFlip={false} offset={3}>
            <Menu className={css({ maxHeight: "50vh", overflowY: "auto" })}>{children}</Menu>
          </Popover>
        </SubmenuTrigger>
      );
    }

    const meta = resolvedByOption.get(item);
    if (!meta) {
      return null;
    }
    if (meta.option.divider) {
      return <Separator key={`${meta.index}-divider`} />;
    }

    const { action, confirmationMessage, disabled } = meta.option;
    const resolvedId = meta.resolvedId ?? `option-${meta.index}`;
    return (
      <MenuItem
        key={resolvedId}
        onAction={() => {
          if (confirmationMessage) {
            setOpenOptionId(resolvedId);
          } else {
            void action(selected);
          }
        }}
        isDisabled={disabled === true}
      >
        {meta.resolvedLabel}
      </MenuItem>
    );
  };

  return (
    <>
      <MenuButton
        label={
          <>
            {t(($) => $.common.bulkActions["Bulk Actions ({count} selected)"], {
              count: selectedCount ?? (selected === "all" ? 0 : selected.length),
            })}{" "}
            <RxTriangleDown fontSize={"16px"} />
          </>
        }
        buttonProps={{
          isDisabled: (selectedCount ?? (selected === "all" ? 1 : selected.length)) === 0 || isDisabled,
          fontSize: "small",
          fontWeight: "bold",
        }}
      >
        <Menu className={css({ width: "var(--trigger-width)", overflowY: "auto" })}>{options.map(renderMenuItem)}</Menu>
      </MenuButton>
      {openOption && (
        <BulkActionModal
          isOpen={!!openOption}
          onOpenChange={(isOpen) => !isOpen && setOpenOptionId(null)}
          onConfirm={() => void openOption.action(selected)}
          disableConfirm={openOption.disableConfirmButton}
        >
          {openOption.actionFormFields}
          {openOption.confirmationMessage}
        </BulkActionModal>
      )}
    </>
  );
};

function BulkActionModal({
  children,
  onConfirm,
  disableConfirm,
  ...props
}: ModalProps & { onConfirm: () => void; disableConfirm?: boolean }) {
  const { t } = useTranslation(["common"]);
  return (
    <Modal
      title={t(($) => $.common.bulkActions["Confirm Bulk Action"])}
      css={{
        width: "60vw",
      }}
      {...props}
    >
      {(opts) => (
        <>
          <VStack gap={"medium"} alignItems={"center"} paddingX="large" overflowX={"hidden"} overflowY="auto">
            {typeof children === "function" ? children(opts) : children}
          </VStack>
          <HStack justifyContent={"space-evenly"} width="100%">
            <Button variant={"secondary"} onPress={() => opts.close()}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button
              onPress={() => {
                onConfirm();
                opts.close();
              }}
              isDisabled={disableConfirm}
            >
              {t(($) => $.common.actions.Confirm)}
            </Button>
          </HStack>
        </>
      )}
    </Modal>
  );
}
